import { useField, useFormikContext } from 'formik'
import { Col, Form } from 'react-bootstrap'
import { snakeCase } from 'lodash'
import { HeadingCombinationSettingType, Orientation } from '../../common/types'
import { getHeadingObjectStyleType, changeHeadingObjectStyleType } from '../ArticleParse'
import { DirtyEffect } from '../CommonComponents'
import type { LayoutableSetting } from '../types'

import { fieldNameWithParent } from './common'

interface EnumValues {
  headingCombinationSettingTypes: Record<string, string>
}

interface Props {
  layoutable: { setting: LayoutableSetting }
  enumValues: EnumValues
  formEditable: boolean
}

interface FormValues {
  rootHeading: { data: { headingOrientation?: string; numberOfOccupyingColumns?: number } }
}

const HORIZONTAL_HEADING_COMBINATION_TYPES = [
  // HeadingCombinationSettingType.HHeadingHalfCenter,
  // HeadingCombinationSettingType.HHeadingHalfTop,
  HeadingCombinationSettingType.HHeadingSmallCenter,
  HeadingCombinationSettingType.HHeadingSmallTop,
  HeadingCombinationSettingType.HHeadingMediumCenter,
  HeadingCombinationSettingType.HHeadingMediumTop,
  HeadingCombinationSettingType.HHeadingLargeCenter,
  HeadingCombinationSettingType.HHeadingLargeTop
]
const ONE_COLUMN_HEADING_COMBINATION_TYPES = [
  HeadingCombinationSettingType.Default,
  HeadingCombinationSettingType.OneHeading,
  HeadingCombinationSettingType.TwoHeadingsPillar,
  HeadingCombinationSettingType.TwoHeadingsSecondary,
  HeadingCombinationSettingType.ThreeHeadings
]
const MORE_COLUMN_HEADING_COMBINATION_TYPES = [
  HeadingCombinationSettingType.Default,
  HeadingCombinationSettingType.OneHeading,
  HeadingCombinationSettingType.TwoHeadingsPillar,
  HeadingCombinationSettingType.TwoHeadingsSecondary,
  HeadingCombinationSettingType.ThreeHeadingsCenter,
  HeadingCombinationSettingType.ThreeHeadingsRight
]

export const HeadingCombinationSettingTypeField = ({ layoutable, enumValues, formEditable }: Props) => {
  const context = useFormikContext<FormValues>()
  const { headingOrientation, numberOfOccupyingColumns } = context.values.rootHeading.data
  const isHorizontal = headingOrientation === Orientation.Horizontal
  const combinationTypes = isHorizontal
    ? HORIZONTAL_HEADING_COMBINATION_TYPES
    : numberOfOccupyingColumns === 1
      ? ONE_COLUMN_HEADING_COMBINATION_TYPES
      : MORE_COLUMN_HEADING_COMBINATION_TYPES
  const combinationTypeSet = new Set<string>(combinationTypes)
  const options = Object.keys(enumValues.headingCombinationSettingTypes).flatMap((k) =>
    combinationTypeSet.has(k) ? (
      <option key={`headingCombinationSettingType-option-${k}`} value={snakeCase(k)}>
        {enumValues.headingCombinationSettingTypes[k]}
      </option>
    ) : (
      []
    )
  )
  const [typeField, typeMeta, typeHelpers] = useField(
    fieldNameWithParent('data.headingCombinationSettingType', 'rootHeading')
  )
  const [_columnsField, _columnsMeta, columnsHelpers] = useField(
    fieldNameWithParent('data.numberOfOccupyingColumns', 'rootHeading')
  )
  const [_rowsField, _rowsMeta, rowsHelpers] = useField(
    fieldNameWithParent('data.numberOfOccupyingRows', 'rootHeading')
  )
  const isDirty = typeField.value !== typeMeta.initialValue
  const onChange: React.ChangeEventHandler<HTMLElement> = (e) => {
    const target = e.target as HTMLSelectElement
    const newTypeValue = target.value
    typeHelpers.setValue(newTypeValue)
    if (isHorizontal) {
      const newStyleType = getHeadingObjectStyleType(newTypeValue)
      const headingNumberOfOccupyingRows = changeHeadingObjectStyleType({
        headingObjectStyleType: newStyleType ?? '',
        setting: layoutable.setting
      })
      const headingNumberOfOccupyingColumns = 1
      columnsHelpers.setValue(headingNumberOfOccupyingColumns)
      rowsHelpers.setValue(headingNumberOfOccupyingRows)
    }
  }
  return (
    <Col sm={8}>
      <DirtyEffect dirty={isDirty}>
        <Form.Control
          as='select'
          type='text'
          size='sm'
          name='headingCombinationSettingType'
          value={typeField.value}
          onChange={onChange}
          onBlur={(_e) => {}}
          isInvalid={undefined}
          disabled={!formEditable}
        >
          {options}
        </Form.Control>
        <Form.Control.Feedback type='invalid'>{undefined}</Form.Control.Feedback>
      </DirtyEffect>
    </Col>
  )
}
